import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

function ChatBot() {
  // Initialize messages from localStorage or use default
  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem('chatHistory');
    return savedMessages ? JSON.parse(savedMessages) : [
      { 
        role: 'assistant', 
        content: 'Hi! I\'m the RoboWhales Scout Assistant. Ask me about team performance, match data, or strategy recommendations!' 
      }
    ];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chatHistory', JSON.stringify(messages));
  }, [messages]);

  // Auto-scroll to bottom of messages (only within container, don't affect page scroll)
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // Simplify the formatMessage function to only handle headings
  const formatMessage = (content) => {
    if (!content) return '';
    
    // Format just the headings with minimal styling
    content = content.replace(/### (.*?)(?:\n|$)/g, '<div class="message-heading-3">$1</div>');
    content = content.replace(/## (.*?)(?:\n|$)/g, '<div class="message-heading-2">$1</div>');
    content = content.replace(/# (.*?)(?:\n|$)/g, '<div class="message-heading-1">$1</div>');
    
    // Convert line breaks to <br> tags
    content = content.replace(/\n/g, '<br>');
    
    // Remove asterisks (keep this standard behavior)
    content = content.replace(/\*/g, '');
    
    return content;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message to chat
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    
    try {
      // Get conversation history (excluding the initial greeting)
      const conversationHistory = messages.length > 1 ? messages : [];
      
      // Use different API URL based on environment
      const apiUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:3002/api/chat'
        : '/api/chat';
      
      console.log("Sending request to:", apiUrl);
      
      // Call the API
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: input,
          conversationHistory: conversationHistory
        }),
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        let errorMessage = 'API request failed';
        let isTimeout = false;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          isTimeout = errorData.timeout || response.status === 504;
          console.error("API error details:", errorData);
        } catch (e) {
          console.error("Couldn't parse error response:", e);
          // If it's a 504, it's definitely a timeout
          if (response.status === 504) {
            errorMessage = 'Request timed out. The chatbot is processing too much data. Try asking a more specific question about specific teams or matches.';
            isTimeout = true;
          }
        }
        
        const error = new Error(errorMessage);
        error.isTimeout = isTimeout;
        throw error;
      }
      
      const data = await response.json();
      console.log("API response:", data);
      
      const aiMessage = { 
        role: 'assistant', 
        content: data.response,
        context: data.context
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error in chat:', error);
      setError(error.message || 'An unknown error occurred');
      
      // Add error message to chat with helpful context
      let errorContent = `Sorry, I encountered an error: ${error.message}.`;
      
      if (error.isTimeout) {
        errorContent = `⏱️ ${error.message}\n\nTip: Try asking about specific teams (e.g., "Tell me about team 9032") or specific matches (e.g., "What happened in match 5?") instead of general questions.`;
      } else {
        errorContent += ' Please try again later.';
      }
      
      const errorMessage = { 
        role: 'assistant', 
        content: errorContent
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // clear chat history
  const clearChatHistory = () => {
    const initialMessage = { 
      role: 'assistant', 
      content: 'Hi! I\'m the RoboWhales Scout Assistant. Ask me about team performance, match data, or strategy recommendations!' 
    };
    setMessages([initialMessage]);
  };

  return (
    <div className="container">
      <div className="chatbot-header">
        <h1 className="chatbot-title">Scout Assistant</h1>
        <button 
          className="clear-chat-button" 
          onClick={clearChatHistory}
          title="Clear chat history"
        >
          Clear Chat
        </button>
      </div>
      <div className="chatbot-container">
        <div className="messages-container">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div 
                className="message-content"
                dangerouslySetInnerHTML={{ __html: 
                  message.role === 'assistant' 
                    ? formatMessage(message.content) 
                    : message.content 
                }}
              />
              {message.context && (
                <div className="message-context">
                  {message.context.teamsAnalyzed?.length > 0 && (
                    <small>Teams analyzed: {message.context.teamsAnalyzed.join(', ')}</small>
                  )}
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="message assistant">
              <div className="message-content loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="error-message">
              Error: {error}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <form onSubmit={handleSubmit} className="input-form">
          <div className="input-bubble">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about team performance, strategy, or match analysis..."
              disabled={isLoading}
            />
            <button 
              type="submit" 
              disabled={isLoading || !input.trim()}
              className="send-button-circle"
              aria-label="Send message"
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" transform="rotate(-90 12 12)"/>
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChatBot; 